import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendBulkSms, getAllPhoneNumbers } from '../../services/SmsService';
import { getAllSmsTemplates } from '../../services/TemplatesService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Custom debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

function NewSms() {
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const navigate = useNavigate();

  // Phone number search functionality
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const searchRef = useRef(null);

  // Click outside handler to close search results
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    async function fetchTemplates() {
      try {
        const data = await getAllSmsTemplates();
        setTemplates(data);
      } catch (err) {
        toast.error('Failed to load SMS templates');
      }
    }
    fetchTemplates();
  }, []);

  // Search for phone numbers
  useEffect(() => {
    async function searchPhoneNumbers() {
      if (debouncedSearchTerm.trim().length < 2) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      setIsSearching(true);
      try {
        const data = await getAllPhoneNumbers({
          pageNumber: 1,
          pageSize: 10,
          searchTerm: debouncedSearchTerm,
          sortBy: 'number',
          sortDescending: false
        });
        setSearchResults(data.items || []);
        setShowSearchResults(true);
      } catch (error) {
        console.error('Failed to search phone numbers:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }

    searchPhoneNumbers();
  }, [debouncedSearchTerm]);

  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setSelectedTemplateId(templateId);
    const template = templates.find(t => String(t.id) === String(templateId));
    if (template) {
      setMessage(template.message || '');
    } else {
      setMessage('');
    }
  };

  const handlePhoneNumberSelect = (phoneNumber) => {
    const currentNumbers = to.split(',').map(num => num.trim()).filter(Boolean);
    if (!currentNumbers.includes(phoneNumber)) {
      const newNumberList = [...currentNumbers, phoneNumber];
      setTo(newNumberList.join(', '));
    }
    setSearchTerm('');
    setShowSearchResults(false);
  };

  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (e.target.value.trim().length >= 2) {
      setShowSearchResults(true);
    } else {
      setShowSearchResults(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!to.trim() || !message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const phoneNumbers = to.split(',').map(num => num.trim()).filter(num => num);
      
      if (phoneNumbers.length === 0) {
        toast.error('Please enter at least one valid phone number');
        return;
      }

      await sendBulkSms({ to: phoneNumbers, message });
      toast.success('SMS sent successfully!');
      setTo('');
      setMessage('');
      setSelectedTemplateId('');
      navigate('/sms-messages');
    } catch (error) {
      console.error('Failed to send SMS:', error);
      toast.error('Failed to send SMS. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4" style={{ maxWidth: 900 }}>
      <ToastContainer />
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Send New SMS</h4>
      </div>
      <form onSubmit={handleSubmit} className="bg-white p-4 rounded shadow-sm border">
        <div className="mb-3">
          <label className="form-label fw-semibold">Select a Template</label>
          <select className="form-select" value={selectedTemplateId} onChange={handleTemplateChange}>
            <option value="">-- No Template --</option>
            {templates.map(t => (
              <option key={t.id} value={t.id}>{t.templateName}</option>
            ))}
          </select>
        </div>
        <div className="mb-3">
          <label htmlFor="smsTo" className="form-label fw-semibold">Phone Numbers *</label>
          <div className="position-relative">
            <textarea
              className="form-control"
              id="smsTo"
              rows="3"
              placeholder="Enter phone numbers separated by commas (e.g., +1234567890, +0987654321)"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              required
            ></textarea>
            <div className="mt-2" ref={searchRef}>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search for existing phone numbers..."
                  value={searchTerm}
                  onChange={handleSearchInputChange}
                  onFocus={() => {
                    if (searchTerm.trim().length >= 2) {
                      setShowSearchResults(true);
                    }
                  }}
                />
                {isSearching && (
                  <span className="input-group-text">
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </span>
                )}
                {searchTerm && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setSearchTerm('');
                      setShowSearchResults(false);
                    }}
                  >
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </div>
              {showSearchResults && searchResults.length > 0 && (
                <div className="position-absolute w-100 mt-1" style={{ zIndex: 1000 }}>
                  <div className="card border shadow-sm">
                    <div className="card-header py-2">
                      <small className="text-muted">Select a phone number to add:</small>
                    </div>
                    <div className="card-body p-0">
                      <div className="list-group list-group-flush">
                        {searchResults.map((phone) => (
                          <button
                            key={phone.id}
                            type="button"
                            className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                            onClick={() => handlePhoneNumberSelect(phone.number)}
                          >
                            <div>
                              <strong>{phone.number}</strong>
                              {phone.fullName && (
                                <div className="text-muted small">{phone.fullName}</div>
                              )}
                            </div>
                            <span className={`badge bg-${phone.isBlocked ? 'danger' : 'success'}`}>
                              {phone.isBlocked ? 'Blocked' : 'Active'}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {showSearchResults && searchResults.length === 0 && debouncedSearchTerm.trim().length >= 2 && !isSearching && (
                <div className="position-absolute w-100 mt-1" style={{ zIndex: 1000 }}>
                  <div className="card border shadow-sm">
                    <div className="card-body p-3 text-center text-muted">
                      No phone numbers found matching "{debouncedSearchTerm}"
                    </div>
                  </div>
                </div>
              )}
            </div>
            <small className="text-muted">Separate multiple numbers with commas. Use the search above to find existing phone numbers.</small>
          </div>
        </div>
        <div className="mb-3">
          <label htmlFor="smsMessage" className="form-label fw-semibold">Message *</label>
          <textarea
            className="form-control"
            id="smsMessage"
            rows="6"
            placeholder="Enter your SMS message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            maxLength="160"
          ></textarea>
          <small className="text-muted">
            <span className={message.length > 140 ? 'text-warning' : 'text-muted'}>
              {message.length}/160 characters
            </span>
            {message.length > 140 && (
              <span className="text-warning ms-2">
                (Message may be split into multiple SMS)
              </span>
            )}
          </small>
        </div>
        <div className="d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Sending...' : 'Send SMS'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default NewSms; 